import { useState } from 'react';
import type { ChecklistRowSave } from '@/api/hooks';
import {
  API_FIELD, DATE_MSG, DONE, ITEMS, itemsDone, optionsFor, trackedItems, type Detail, type Grouped, type ItemCol,
} from '@/lib/submissions';

// Submitted Forms checklist editing, shared by the list (inline, like Pending Requests)
// and the View Form page: unsaved edits per row, Pending's Date Completed rule, and the
// rows to send on Submit.

type Key = ItemCol | 'DateCompleted';

export function useChecklistEdits() {
  const [edits, setEdits] = useState<Record<string, Partial<Record<Key, string>>>>({});
  const valueOf = (d: Detail, c: Key) => edits[d.key]?.[c] ?? d.values[c] ?? '';
  // Current values for a row (saved values with unsaved edits on top).
  const current = (d: Detail): Detail['values'] => ({ ...d.values, ...edits[d.key] });
  const setValue = (formType: string, d: Detail, c: Key, v: string) =>
    setEdits((s) => {
      const next = { ...s[d.key], [c]: v };
      // Like Pending: a row whose tracked items are no longer all Yes / N/A loses its date.
      if (c !== 'DateCompleted' && !itemsDone(formType, { ...d.values, ...next })) next.DateCompleted = '';
      return { ...s, [d.key]: next };
    });
  // Pending's rule: once the tracked items are all Yes / N/A, Date Completed is required.
  const needsDate = (formType: string, d: Detail) => {
    const t = trackedItems(formType);
    return t.length > 0 && t.every((c) => DONE.includes(valueOf(d, c))) && !valueOf(d, 'DateCompleted');
  };
  const edited = (groups: Grouped[]) =>
    groups.filter((g) => !g.completed).flatMap((g) => g.details.filter((d) => !!edits[d.key]).map((d) => ({ g, d })));
  return {
    edits,
    dirty: Object.keys(edits).length > 0,
    reset: () => setEdits({}),
    valueOf,
    current,
    setValue,
    needsDate,
    /** Edited rows still missing their Date Completed (Submit is blocked). */
    missingDate: (groups: Grouped[]) => edited(groups).filter(({ g, d }) => needsDate(g.formType, d)),
    /** The edited rows, as the API save expects them. */
    toSave: (groups: Grouped[]): ChecklistRowSave[] =>
      edited(groups).map(({ g, d }) => {
        const row: ChecklistRowSave = { submissionId: g.id, detailType: d.detailType, detailId: d.detailId };
        for (const c of ITEMS[g.formType] ?? []) (row as unknown as Record<string, string | null>)[API_FIELD[c]] = valueOf(d, c) || null;
        row.dateCompleted = valueOf(d, 'DateCompleted') || null;
        return row;
      }),
  };
}
export type ChecklistEdits = ReturnType<typeof useChecklistEdits>;

/** One row's checklist cells: a dropdown per item, then Date Completed. Read-only once completed. */
export function ChecklistCells({ g, d, ce, cellClass = 'py-1 pr-2' }: {
  g: Grouped; d: Detail; ce: ChecklistEdits; cellClass?: string;
}) {
  const ready = itemsDone(g.formType, ce.current(d));
  const dateTip = g.formType === 'Discontinuation'
    ? 'Set all six statuses to Yes or N/A first'
    : 'Set Maconomy and Constant to Yes or N/A first';
  return (
    <>
      {(ITEMS[g.formType] ?? []).map((c) => (
        <td key={c} className={cellClass}>
          <select className="input w-[4.75rem] px-2 py-1" disabled={g.completed} value={ce.valueOf(d, c)}
            onChange={(e) => ce.setValue(g.formType, d, c, e.target.value)}>
            {optionsFor(g.formType, c).map((o) => <option key={o} value={o}>{o || '—'}</option>)}
          </select>
        </td>
      ))}
      <td className={cellClass}>
        <div className="flex items-center gap-1">
          <input type="date" className="input w-[8.5rem] px-2 py-1"
            disabled={g.completed || !ready}
            title={!ready ? dateTip : ''}
            value={ready ? ce.valueOf(d, 'DateCompleted') : ''}
            onChange={(e) => ce.setValue(g.formType, d, 'DateCompleted', e.target.value)} />
          {!g.completed && ce.needsDate(g.formType, d) && <span title={DATE_MSG} className="font-bold text-orange-500">!</span>}
        </div>
      </td>
    </>
  );
}
